import {useMemo} from 'react'
import type {Thread} from '@github-ui/diff-lines/conversation-types'

import {filterConversationItems, filterThreads} from './utils/filter-items'
import {buildConversationItems, buildReviewThreadItems, type ConversationItem} from './utils/build-conversation-items'

import {useDiffPathOrderMap, usePullRequestBasePath, useThreads} from '../../stores/layout-selectors'
import {sortConversationItemsByDate, sortThreadItemsByDiffOrder} from './utils/sort-items'
import {getConversationItemsAuthors, type AuthorFilterItem} from './utils/get-author-filters'
import {CommentsSort} from './use-comments-sort-state'
import {defaultFilterStates, type CommentsFilterState} from './use-comments-filter-state'
import {useInfiniteConversationCommentsQuery} from '../../page-data/loaders/use-conversation-comments'
import type {CommentsDisplayMode} from './utils/display-mode'
import {getFirstComment} from '../../page-data/payloads/thread-previews'
import {ViewMode} from './utils/view-mode'

export interface UseFilteredCommentsDataOptions {
  /** Current filter state. */
  filterState: CommentsFilterState
  /** Current sort direction. */
  sort: CommentsSort
  displayMode: CommentsDisplayMode
}

export interface FilteredCommentsData {
  /** Unique authors of unfiltered items, for the author filter. */
  authorList: AuthorFilterItem[]
  /** Conversation items matching the conversation filter and sort direction. */
  filteredConversationItems: ConversationItem[]
  /** Total number of conversation items in this PR. */
  totalConversationItemsCount: number
  /** Whether every loaded review thread is resolved. */
  allReviewThreadsResolved: boolean
  /** Whether the current user authored a review thread, or undefined if unknown. */
  hasCurrentUserReviewThreads?: boolean
}

const emptyItems = {
  unfilteredItems: [],
  filteredSortedItems: [],
  authorItems: [],
}

export function useFilteredCommentsData({
  filterState,
  sort,
  displayMode,
}: UseFilteredCommentsDataOptions): FilteredCommentsData {
  const threadPreviews = useThreads()
  const currentUserLogin = defaultFilterStates()[ViewMode.Mine].selectedAuthors?.[0]
  const {allReviewThreadsResolved, hasCurrentUserReviewThreads} = getReviewThreadFacts(threadPreviews, currentUserLogin)

  const diffPathOrderMap = useDiffPathOrderMap()

  const basePath = usePullRequestBasePath()

  const conversationComments = useInfiniteConversationCommentsQuery({
    basePath,
    enabled: displayMode === 'conversation',
  }).data?.comments

  // Review thread mode displays threads as flat list without issue comments or review summaries
  const reviewThreadItems = useMemo(() => {
    if (displayMode !== 'review-threads') return emptyItems

    const unfilteredItems = buildReviewThreadItems(threadPreviews)
    const filteredItems = filterConversationItems(unfilteredItems, filterState)
    const filteredSortedItems =
      sort === 'diff-order'
        ? sortThreadItemsByDiffOrder(filteredItems, diffPathOrderMap)
        : sortConversationItemsByDate(filteredItems, sort)

    return {unfilteredItems, filteredSortedItems, authorItems: unfilteredItems}
  }, [filterState, threadPreviews, diffPathOrderMap, sort, displayMode])

  // Conversation mode displays full list of comments and reviews with threads
  const conversationItems = useMemo(() => {
    // Diff-order sort is not supported for conversation mode
    if (displayMode !== 'conversation' || sort === 'diff-order') return emptyItems

    // Items are built using filtered threads so that we don't show non-matching threads inside matching review items
    const threadsForItems = filterThreads(threadPreviews, filterState)
    const authorCountFilterState = {
      ...filterState,
      selectedAuthors: undefined,
    }
    const authorThreads = filterThreads(threadPreviews, authorCountFilterState)

    const unfilteredItems = buildConversationItems(conversationComments ?? [], threadPreviews)
    const filterableItems = buildConversationItems(conversationComments ?? [], threadsForItems)
    const authorItems = buildConversationItems(conversationComments ?? [], authorThreads)
    const filteredSortedItems = sortConversationItemsByDate(filterConversationItems(filterableItems, filterState), sort)

    return {unfilteredItems, filteredSortedItems, authorItems}
  }, [displayMode, threadPreviews, filterState, conversationComments, sort])

  // Even though we resolve to a single value, the two separate memos help minimize dependencies per-mode to reduce
  // unnecessary calculations and renders, at a small complexity cost
  const resolvedItems = displayMode === 'review-threads' ? reviewThreadItems : conversationItems

  const authorList = useMemo(() => {
    const authorCountFilterState = {
      ...filterState,
      selectedAuthors: undefined,
    }
    const filteredItemsForAuthorCounts = filterConversationItems(resolvedItems.authorItems, authorCountFilterState)
    return getConversationItemsAuthors(resolvedItems.authorItems, filteredItemsForAuthorCounts)
  }, [filterState, resolvedItems.authorItems])

  return {
    filteredConversationItems: resolvedItems.filteredSortedItems,
    authorList,
    totalConversationItemsCount: resolvedItems.unfilteredItems?.length ?? 0,
    allReviewThreadsResolved,
    hasCurrentUserReviewThreads,
  }
}

export function getReviewThreadFacts(threadPreviews: Thread[], currentUserLogin?: string) {
  return {
    allReviewThreadsResolved: threadPreviews.length > 0 && threadPreviews.every(thread => thread.isResolved === true),
    hasCurrentUserReviewThreads: currentUserLogin
      ? threadPreviews.some(thread => getFirstComment(thread)?.author?.login === currentUserLogin)
      : undefined,
  }
}
